/**
 * Labstep
 *
 * @module grid/SmartTableProtocolCondition
 * @desc DataGrid for SmartTable ProtocolCondition
 */

import type { AgGridReact } from 'ag-grid-react';
import { EntityEmptyState } from 'labstep-web/components/Entity/EmptyState';
import { ProtocolConditionActionCreateBulk } from 'labstep-web/components/ProtocolCondition/Action/Create/Bulk';
import { DataGrid } from 'labstep-web/core/DataGrid';
import { LoaderWithProgress } from 'labstep-web/core/DataGrid/Overlay/LoaderProgress';
import type { IDataGridProps } from 'labstep-web/core/DataGrid/types';
import { useExportProgressContext } from 'labstep-web/grid/Index/components/DataGridExportAsCSV/context';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolCondition/components/ColumnHeader';
import { GridSmartTableStatusBar } from 'labstep-web/grid/SmartTableProtocolCondition/components/StatusBar';
import {
  useBulkSelect,
  useInfiniteScroll,
  useSideBar,
} from 'labstep-web/grid/SmartTableProtocolCondition/hooks';
import { GridSmartTableProtocolConditionService } from 'labstep-web/grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition.service';
import { GridSmartTableProtocolConditionColDefService } from 'labstep-web/grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition-coldef.service';
import { GridService } from 'labstep-web/grid/services/grid.service';
import { ProtocolCondition } from 'labstep-web/models/protocol-condition.model';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './styles.module.scss';
import type { GridSmartTableProps } from './types';

// Define the cell state interface
export type CellState = 'loading' | 'error' | 'success';

// Define the grid context interface
interface GridContext {
  cellStates: {
    [entityId: string]: {
      [colId: string]: CellState;
    };
  };
  setCellState: (
    entityId: string,
    colId: string,
    state: CellState,
  ) => void;
  getCellState: (entityId: string, colId: string) => CellState | null;
}

export const GridSmartTable: React.FC<GridSmartTableProps> = ({
  experimentOrProtocol,
  entities,
  loadMore,
  status,
  entityView,
  fullscreen = false,
  withEmptyState,
  ...rest
}) => {
  // Infinite scroll, enabled by passing a loadMore function
  const infiniteScrollProps = useInfiniteScroll(loadMore);

  // Bulk select on row selection
  const bulkSelectProps = useBulkSelect();

  // Initialise side bar
  useSideBar(entityView, experimentOrProtocol, fullscreen);

  // Set column header custom component
  const components = useMemo(
    () => ({
      agColumnHeader: GridSmartTableColumnHeader,
    }),
    [],
  );

  // Cell state management
  const [cellStates, setCellStates] = useState<
    GridContext['cellStates']
  >({});

  // Grid context with cell state management functions
  const gridContext: GridContext = useMemo(
    () => ({
      cellStates,
      setCellState: (
        entityId: string,
        colId: string,
        state: CellState | null,
      ) => {
        setCellStates((prev) => {
          if (state === null) {
            // Remove the key when state is null
            const { [colId]: _, ...restCols } = prev[entityId] || {};
            const updatedEntity = { ...restCols };

            // If no columns left for this entity, remove the entity entirely
            if (Object.keys(updatedEntity).length === 0) {
              const { [entityId]: __, ...restEntities } = prev;
              return restEntities;
            }

            return {
              ...prev,
              [entityId]: updatedEntity,
            };
          }

          // Set the state normally
          return {
            ...prev,
            [entityId]: {
              ...prev[entityId],
              [colId]: state,
            },
          };
        });
      },
      getCellState: (entityId: string, colId: string) => {
        return cellStates[entityId]?.[colId] || null;
      },
    }),
    [cellStates],
  );

  // Get the row id from the idAttr field - Required for AG Grid
  const getRowId = useCallback<
    NonNullable<IDataGridProps['getRowId']>
  >((params) => params.data.idAttr, []);

  const exportProgressContext = useExportProgressContext();

  useEffect(() => {
    if (exportProgressContext) {
      exportProgressContext.setExportProgress((prev) => ({
        ...prev,
        initialEntitiesCount: entities.length,
      }));
    }
  }, [entities.length]);
  const statusBarChildren = useMemo(() => {
    return GridSmartTableProtocolConditionService.getStatusBarChildren(
      {
        experimentOrProtocol,
      },
    );
  }, [experimentOrProtocol]);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: GridSmartTableStatusBar,
          statusPanelParams: {
            children: statusBarChildren,
          },
          align: 'left',
        },
      ],
    };
  }, [statusBarChildren]);

  return (
    <DataGrid
      key={entityView?.column_definition_ids?.join('-')}
      height={'100%'}
      width={'100%'}
      columnDefs={GridSmartTableProtocolConditionColDefService.getColDefs(
        entityView,
        experimentOrProtocol,
        fullscreen,
      )}
      getRowId={getRowId}
      rowData={entities}
      status={status}
      components={components}
      context={gridContext}
      {...infiniteScrollProps}
      {...bulkSelectProps}
      onColumnResized={(params) => {
        const gridService = new GridService(
          params as unknown as AgGridReact,
        );
        entityView.state = gridService.getState();
        gridService.setState(entityView.state);
      }}
      onGridReady={(params): void => {
        infiniteScrollProps.onGridReady?.(params);
        const gridService = new GridService(
          params as unknown as AgGridReact,
        );
        gridService.setState(entityView.state);
      }}
      statusBar={statusBar}
      {...rest}
      progressComponent={
        exportProgressContext &&
        exportProgressContext.exportProgress.isExporting && (
          <LoaderWithProgress
            value={exportProgressContext.exportProgress.value}
            total={exportProgressContext.exportProgress.total}
            hasFailed={exportProgressContext.exportProgress.hasFailed}
          />
        )
      }
      hidden={false}
      className={fullscreen ? styles.fullscreen : styles.inline}
      emptyState={
        withEmptyState && (
          <EntityEmptyState
            entityName={ProtocolCondition.entityName}
            searchParams={{}}
            action={
              <ProtocolConditionActionCreateBulk
                protocol={experimentOrProtocol}
                actionComponentProps={{
                  type: 'text',
                  icon: 'plus',
                }}
              />
            }
          />
        )
      }
    />
  );
};
